import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Eye, EyeOff, CheckCircle, X } from 'lucide-react';
import { BASE_URL } from './config';
import { saveAuth } from './utils/auth';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  // step: null | 'email' | 'otp' | 'newPassword' | 'success'
  const [fpStep, setFpStep] = useState(null);
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpPassword, setFpPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('logout') === 'true') {
      localStorage.clear();
    }
  }, []);

  // ── Forgot password handlers ──────────────────────────────────────────────

  const openForgotPassword = () => {
    setFpStep('email');
    setFpEmail('');
    setFpOtp('');
    setFpPassword('');
    setFpConfirmPassword('');
    setFpShowPassword(false);
    setFpError('');
  };

  const closeForgotPassword = () => {
    setFpStep(null);
    setFpError('');
  };

  // Strip trailing slash from BASE_URL to avoid double-slash URLs
  const apiBase = BASE_URL.replace(/\/$/, '');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFpLoading(true);
    setFpError('');
    try {
      await axios.post(`${apiBase}/api/superadmin/password-reset/otp/`, {
        email: fpEmail.toLowerCase().trim(),
      });
      setFpStep('otp');
    } catch (err) {
      // err.response exists for 4xx/5xx from server
      // err.response is undefined for network errors (no connection, CORS, redirect)
      const serverMsg = err.response?.data?.error;
      console.error('OTP send error:', err.response?.status, err.response?.data);
      if (serverMsg) {
        setFpError(serverMsg);
      } else if (err.response?.status === 404) {
        setFpError('No admin account found with this email address.');
      } else if (!err.response) {
        setFpError('Could not reach the server. Please check your connection.');
      } else {
        setFpError('Something went wrong. Please try again.');
      }
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (fpOtp.length !== 6) {
      setFpError('Please enter the complete 6-digit OTP.');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      await axios.post(`${apiBase}/api/superadmin/password-reset/verify-otp/`, {
        email: fpEmail.toLowerCase().trim(),
        otp: fpOtp,
      });
      setFpStep('newPassword');
    } catch (err) {
      setFpError(err.response?.data?.error || 'Invalid or expired OTP. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (fpPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match.');
      return;
    }
    if (fpPassword.length < 6) {
      setFpError('Password must be at least 6 characters.');
      return;
    }
    setFpLoading(true);
    setFpError('');
    try {
      await axios.post(`${apiBase}/api/superadmin/password-reset/verify/`, {
        email: fpEmail.toLowerCase().trim(),
        otp: fpOtp,
        password: fpPassword,
      });
      setFpStep('success');
    } catch (err) {
      setFpError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${BASE_URL}/api/superadmin/login/`, {
        email,
        password,
      });

      const token = res.data.access;
      const refreshToken = res.data.refresh;
      const role = res.data.role;
      const hospitalId = res.data.hospitalId;
      const clinicId = res.data.clinicId;
      const userId = res.data.userId;
      
      // Save to localStorage
      saveAuth(token, role, hospitalId, clinicId, userId);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      
      setError('');
      // Use replace to prevent going back to login page
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3">
            <img src="/logo-footer.png" alt="ClinicalAssist" className="h-20" />
          </div>
          {/* <h1 className="text-2xl font-semibold text-slate-800">ClinicalAssist</h1> */}
          <p className="text-sm text-slate-600 mt-1">Healthcare Management System</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-5">Welcome Back</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="w-full pl-9 pr-10 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {/* Forgot Password Link */}
              <div className="text-right mt-1.5">
                <button
                  type="button"
                  onClick={openForgotPassword}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-5 text-center">
            <p className="text-slate-600 text-xs">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Register
              </button>
            </p>
          </div>

          {/* Demo Accounts */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Demo Accounts:</p>
            <div className="space-y-1 text-xs text-slate-600">
              <p>• pixeltre@gmail.com</p>
              <p>• bala@gmail.com</p>
              <p>• bala123@gmail.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* ── Forgot Password Modal ──────────────────────────────────────────── */}
      {fpStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded border border-slate-200 w-full max-w-sm p-6 relative">

            {/* Close button */}
            <button
              onClick={closeForgotPassword}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* ── Step 1: Enter email ── */}
            {fpStep === 'email' && (
              <>
                <h3 className="text-base font-semibold text-slate-800 mb-1">Forgot Password</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Enter your email address and we'll send you an OTP to reset your password.
                </p>
                <form onSubmit={handleSendOtp} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={fpEmail}
                        onChange={e => setFpEmail(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                  {fpError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {fpError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={fpLoading}
                    className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
                  >
                    {fpLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              </>
            )}

            {/* ── Step 2: Enter OTP ── */}
            {fpStep === 'otp' && (
              <>
                <h3 className="text-base font-semibold text-slate-800 mb-1">Enter OTP</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Enter the 6-digit OTP sent to{' '}
                  <span className="font-medium text-slate-700">{fpEmail}</span>.
                  It expires in 5 minutes.
                </p>
                <form onSubmit={handleVerifyOtp} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      OTP Code
                    </label>
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={fpOtp}
                      onChange={e => { setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setFpError(''); }}
                      required
                      maxLength={6}
                      autoFocus
                      className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm tracking-widest text-center font-mono"
                    />
                  </div>
                  {fpError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {fpError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={fpLoading || fpOtp.length !== 6}
                    className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
                  >
                    {fpLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setFpStep('email'); setFpOtp(''); setFpError(''); }}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 mt-1"
                  >
                    ← Change email / Resend OTP
                  </button>
                </form>
              </>
            )}

            {/* ── Step 3: New password ── */}
            {fpStep === 'newPassword' && (
              <>
                <h3 className="text-base font-semibold text-slate-800 mb-1">Set New Password</h3>
                <p className="text-xs text-slate-500 mb-4">
                  OTP verified. Enter your new password below.
                </p>
                <form onSubmit={handleResetPassword} className="space-y-3">
                  {/* New password */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={fpShowPassword ? 'text' : 'password'}
                        placeholder="Min. 6 characters"
                        value={fpPassword}
                        onChange={e => { setFpPassword(e.target.value); setFpError(''); }}
                        required
                        autoFocus
                        className="w-full pl-9 pr-10 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setFpShowPassword(!fpShowPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {fpShowPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {/* Confirm password */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={fpShowPassword ? 'text' : 'password'}
                        placeholder="Re-enter password"
                        value={fpConfirmPassword}
                        onChange={e => { setFpConfirmPassword(e.target.value); setFpError(''); }}
                        required
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                  {fpError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {fpError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={fpLoading}
                    className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
                  >
                    {fpLoading ? 'Saving...' : 'Save New Password'}
                  </button>
                </form>
              </>
            )}

            {/* ── Step 3: Success ── */}
            {fpStep === 'success' && (
              <div className="text-center py-4">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-slate-800 mb-1">Password Reset!</h3>
                <p className="text-xs text-slate-500 mb-5">
                  Your password has been updated. You can now log in with your new password.
                </p>
                <button
                  onClick={closeForgotPassword}
                  className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors text-sm"
                >
                  Back to Login
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}